import {
  type AllowedImageContentType,
  FACE_ASSET_CONTENT_TYPES,
  type FaceAssetUploadPurpose,
  UPLOAD_PURPOSE_SEGMENTS,
} from '@repo/api-contracts';
import type { CompositeAssetKind } from '@/entities/edit-area';

/** 합성 에셋 종류 하나의 업로드 어휘 — 용도·라벨·키 세그먼트·허용 형식. 계약 표(storage.ts)에서 끌어온다. */
export interface CompositeAssetKindSpec {
  readonly purpose: FaceAssetUploadPurpose;
  readonly label: string;
  /** 확정 키의 세그먼트(face-masks·face-shadings·face-safe-areas). 다른 세그먼트 키는 반영하지 않는다. */
  readonly segment: string;
  /** 파일 선택 input 의 accept — 계약 표 FACE_ASSET_CONTENT_TYPES[purpose] 그대로. */
  readonly accept: string;
  readonly contentTypeMessage: string;
  /** 카드에 보이는 한 줄 설명. */
  readonly description: string;
}

const FORMAT_NAMES: Readonly<Record<AllowedImageContentType, string>> = {
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/webp': 'WebP',
};

const specOf = (
  purpose: Exclude<FaceAssetUploadPurpose, 'EDIT_FACE_PHOTO'>,
  label: string,
  description: string,
): CompositeAssetKindSpec => {
  const contentTypes = FACE_ASSET_CONTENT_TYPES[purpose];
  return {
    purpose,
    label,
    segment: UPLOAD_PURPOSE_SEGMENTS[purpose],
    accept: contentTypes.join(','),
    contentTypeMessage: `${contentTypes.map((type) => FORMAT_NAMES[type]).join('·')} 형식만 업로드할 수 있습니다.`,
    description,
  };
};

/** 마스크는 필수, 음영·안내선은 선택이다(ADR-0007 D6·D14). 마스크·음영은 알파가 있는 PNG·WebP, 안내선은 PNG 만 받는다. */
export const COMPOSITE_ASSET_KINDS: Readonly<Record<CompositeAssetKind, CompositeAssetKindSpec>> = {
  mask: specOf('EDIT_FACE_MASK', '마스크', '필수 · 제품 실루엣(알파)로 미리보기를 자릅니다.'),
  shading: specOf('EDIT_FACE_SHADING', '음영', '선택 · 사진 위에 곱해지는 명암(알파).'),
  safeArea: specOf('EDIT_FACE_SAFE_AREA', '안내선', '선택 · 인쇄 가능 범위 안내선(PNG).'),
};
